import { ApiClient } from '@provider-portal/common/api';
import { GetAccountInfoResponse } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProvider/apis';
import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { selectAssignedProviderIds } from 'rdx/account/selectors';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { ACCOUNT_STATE_PERSIST_KEY } from 'rdx/reducer';
import { GlobalAppState } from 'rdx/state';
import { getMainEntryWidgetUrlAsyncAction } from 'rdx/support';
import { requestTargetingParametersAsyncAction } from 'rdx/targetingParameters/actions';

import {
  requestAccountCampaignInfoAsyncAction,
  requestAccountInfoAsyncAction,
  selectProviderAction,
} from './actions';
import { resolveProviderSwitchLocation } from './providerSwitchLocation';
import {
  GetProviderPortalAccountInfoResponse,
  GetProviderPortalCampaignAccountInfoResponse,
  ProviderPortalAssignedProvider,
  VendorAssignedProvider,
} from './types';
import { LocaleType } from '@provider-portal/common/utils/translations_types';

const getAccountInfo = async (
  apiClient: ApiClient,
): Promise<GetProviderPortalAccountInfoResponse> => {
  const getAccountInfoResponse: GetAccountInfoResponse =
    await apiClient.deliveryProvider.getAccountInfo();

  const bulgariaCityIds =
    getAccountInfoResponse.account_info.bulgaria_euro_change_data?.city_ids_bulgaria_euro_change ??
    [];
  const bulgariaCurrencySwitchTimestamp =
    getAccountInfoResponse.account_info.bulgaria_euro_change_data?.currency_change_timestamp ??
    null;

  const providerAssignments: ProviderPortalAssignedProvider[] =
    getAccountInfoResponse.account_info.assignments.map((assignment) => {
      return {
        provider_id: assignment.provider_id,
        provider_name: assignment.provider_name,
        vendor_id: assignment.vendor_id,
        vendor_name: assignment.vendor_name,
        city_id: assignment.city_id,
        country_code: assignment.country_code ?? '',
        country_support_email: assignment.country_support_email,
        currency: assignment.currency,
        timezone: assignment.timezone ?? '',
        price_format_setting: assignment.price_format_setting,
        city_locales: assignment.city_locales as LocaleType[],
        city_main_locale: assignment.city_main_locale as LocaleType,
        campaigns_settings: {
          can_self_assign_campaign: assignment.food_partner_portal_setup.can_self_assign_campaign,
          can_manage_promotions: assignment.food_partner_portal_setup.can_manage_promotions,
          is_menu_inflation: assignment.food_partner_portal_setup.is_menu_inflation,
          // TODO : integrate getAccountInfo CAMLE-2907
          can_setup_bolt_plus_promotions: false,
          can_manage_smart_promotions: false,
        },
        menu_editor_settings: assignment.menu_editor_settings,
        has_city_subject_to_bulgaria_euro_change: bulgariaCityIds.includes(assignment.city_id),
        bulgaria_currency_change_timestamp: bulgariaCurrencySwitchTimestamp,
        dine_out_settings: assignment.dine_out_settings,
        deductions_report_settings: assignment.deductions_report_settings,
        nutritional_data_settings: getAccountInfoResponse.account_info.nutritional_data_settings,
        signup_source: assignment.signup_source,
        lifecycle_status: assignment.lifecycle_status,
        is_performance_tier_available: assignment.is_performance_tier_available,
      };
    });

  const assignedProvidersByVendorId: Map<number, VendorAssignedProvider> = new Map<
    number,
    VendorAssignedProvider
  >();

  for (const provider of providerAssignments) {
    const vendorProviders: VendorAssignedProvider = assignedProvidersByVendorId.get(
      provider.vendor_id,
    ) || { vendor_id: provider.vendor_id, vendor_name: provider.vendor_name, providers: [] };
    vendorProviders.providers.push(provider);
    assignedProvidersByVendorId.set(vendorProviders.vendor_id, vendorProviders);
  }

  return {
    account_info: {
      account_data: getAccountInfoResponse.account_info.account_data,
      assignments: providerAssignments,
      assignedProvidersByVendor: assignedProvidersByVendorId,
    },
  };
};

const getAccountCampaignInfo = async (
  apiClient: ApiClient,
  providerIDs: number[],
): Promise<GetProviderPortalCampaignAccountInfoResponse> => {
  return apiClient.deliveryProviderPortal.getCampaignsPermissionsForProviders({
    provider_ids: providerIDs,
  });
};

const getAccountInfoEpic = (
  action$: Observable<GlobalAppActions>,
  _state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountInfoAsyncAction.request)),
    switchMap(() =>
      from(getAccountInfo(apiClient)).pipe(
        map((accountInfo) => requestAccountInfoAsyncAction.success(accountInfo)),
        catchError((err) => of(requestAccountInfoAsyncAction.failure(err))),
      ),
    ),
  );
};

const getAccountCampaignInfoEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountCampaignInfoAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const providerIDs = selectAssignedProviderIds(state);
      return from(getAccountCampaignInfo(apiClient, providerIDs)).pipe(
        map((campaignInfo) => requestAccountCampaignInfoAsyncAction.success(campaignInfo)),
        catchError((err) => of(requestAccountCampaignInfoAsyncAction.failure(err))),
      );
    }),
  );
};

const triggerCampaignInfoRequestEpic = (
  action$: Observable<GlobalAppActions>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountInfoAsyncAction.success)),
    map(() => requestAccountCampaignInfoAsyncAction.request()),
  );
};
const selectProviderEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(selectProviderAction)),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      // ATTENTION - BYPASS REDUX-PERSIST ASYNC BEHAVIOUR
      // Before changing provider we have to manually set the value in local storage
      // That's because on reload, it is possible it will hydrate with the old value from localStorage
      const localStorageItem = localStorage.getItem(ACCOUNT_STATE_PERSIST_KEY);
      const accountStateObject = JSON.parse(localStorageItem ?? 'null') || {};
      accountStateObject.selectedProvider = state.account.selectedProvider;
      const accountStateOverride = JSON.stringify(accountStateObject);
      localStorage.setItem(ACCOUNT_STATE_PERSIST_KEY, accountStateOverride);

      /*
       * On selected provider change, we need to re-fetch data like history, analytics, etc.
       *
       * Since the associated redux actions of these live within the stores of different lattice modules,
       * a page reload is needed to force a re-render and therefore re-fetch of data,
       * for whatever page the user might be viewing.
       *
       * Campaign pages that keep a signup id are the exception: reload would keep that id.
       * */
      const nextPath = resolveProviderSwitchLocation(window.location.pathname);
      if (nextPath) {
        window.location.replace(nextPath);
      } else {
        window.location.reload();
      }
      return of(
        requestTargetingParametersAsyncAction.request({}),
        getMainEntryWidgetUrlAsyncAction.request(),
      );
    }),
  );
};

export const accountEpic = combineEpics(
  selectProviderEpic,
  getAccountInfoEpic,
  getAccountCampaignInfoEpic,
  triggerCampaignInfoRequestEpic,
);
